import {
  ORG_ID,
  SITE_ID,
  SOFTWARE_ID,
  FOUNDER_ID,
  abs,
  webPageId,
  breadcrumbId,
  articleId,
} from './entities';

type JsonLdObject = Record<string, unknown>;
type ObjRef = { '@id': string };

/**
 * Default CSS selectors that AI assistants target for voice answers.
 * Add one of these classes to the opening paragraph / direct-answer
 * block of any page you want AI-citable.
 */
export const DEFAULT_SPEAKABLE_SELECTORS: readonly string[] = [
  '.speakable-summary',
  '.speakable-answer',
  'h1',
];

/**
 * Build a WebPage node with stable @id, speakable CSS selectors, and
 * dateModified freshness. Every content page should emit one.
 *
 * `lastModified` is emitted only when a content-specific date is supplied.
 * Never use build time as editorial freshness: that creates a false change
 * signal on every deployment.
 */
export function buildWebPageSchema(opts: {
  path: string;
  name: string;
  description?: string;
  inLanguage?: string;
  lastModified?: Date | string;
  primaryImage?: string;
  speakable?: readonly string[];
  about?: string; // @id reference, defaults to SOFTWARE_ID
  isPartOf?: string; // @id reference, defaults to SITE_ID
  breadcrumb?: string; // @id reference, defaults to breadcrumbId(path)
  publisher?: string; // @id reference, defaults to ORG_ID
}): JsonLdObject {
  const selectors = opts.speakable ?? DEFAULT_SPEAKABLE_SELECTORS;

  const node: JsonLdObject = {
    '@type': 'WebPage',
    '@id': webPageId(opts.path),
    url: abs(opts.path),
    name: opts.name,
    inLanguage: opts.inLanguage ?? 'en-US',
    isPartOf: { '@id': opts.isPartOf ?? SITE_ID } satisfies ObjRef,
    about: { '@id': opts.about ?? SOFTWARE_ID } satisfies ObjRef,
    breadcrumb: { '@id': opts.breadcrumb ?? breadcrumbId(opts.path) } satisfies ObjRef,
    publisher: { '@id': opts.publisher ?? ORG_ID } satisfies ObjRef,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: [...selectors],
    },
  };

  if (opts.lastModified) {
    node.dateModified =
      typeof opts.lastModified === 'string'
        ? opts.lastModified
        : opts.lastModified.toISOString();
  }

  if (opts.description) node.description = opts.description;
  if (opts.primaryImage) {
    node.primaryImageOfPage = {
      '@type': 'ImageObject',
      url: opts.primaryImage.startsWith('http') ? opts.primaryImage : abs(opts.primaryImage),
    };
  }

  return node;
}

/**
 * Build a BreadcrumbList with proper @id referencing the route's WebPage.
 * Pass the visible breadcrumb items; the helper assembles the JSON-LD.
 */
export function buildBreadcrumbSchema(opts: {
  path: string;
  items: { name: string; url: string }[];
}): JsonLdObject {
  return {
    '@type': 'BreadcrumbList',
    '@id': breadcrumbId(opts.path),
    itemListElement: opts.items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : abs(item.url),
    })),
  };
}

/**
 * Build an Article / BlogPosting node with founder E-E-A-T bylines,
 * dateModified freshness, and a linked WebPage + BreadcrumbList in @graph.
 *
 * Use for /blog/[slug], /guides/*, /reports/*, /tools/* content pages
 * — anything that is primarily an editorial piece rather than a product
 * landing page.
 */
export function buildArticleSchema(opts: {
  path: string;
  headline: string;
  description: string;
  datePublished: Date | string;
  dateModified?: Date | string;
  image: string;
  authorName?: string;
  authorRole?: string;
  authorUrl?: string;
  authorSlug?: string;
  articleBody?: string;
  type?: 'Article' | 'BlogPosting' | 'NewsArticle';
}): JsonLdObject {
  const toIso = (v: Date | string): string => (typeof v === 'string' ? v : v.toISOString());
  const published = toIso(opts.datePublished);
  const modified = opts.dateModified ? toIso(opts.dateModified) : published;
  const image = opts.image.startsWith('http') ? opts.image : abs(opts.image);

  // When authorSlug is provided, reference the author page's Person @id
  // so Google can connect blog post authorship to the full Person entity.
  const author = opts.authorSlug
    ? { '@id': `https://bizrnr.com/author/${opts.authorSlug}#person` }
    : opts.authorName
      ? {
          '@type': 'Person',
          name: opts.authorName,
          ...(opts.authorRole ? { jobTitle: opts.authorRole } : {}),
          ...(opts.authorUrl ? { url: opts.authorUrl } : {}),
        }
      : { '@id': FOUNDER_ID };

  return {
    '@type': opts.type ?? 'Article',
    '@id': articleId(opts.path),
    mainEntityOfPage: { '@id': webPageId(opts.path) } satisfies ObjRef,
    headline: opts.headline,
    description: opts.description,
    image: [image],
    datePublished: published,
    dateModified: modified,
    author,
    publisher: { '@id': ORG_ID } satisfies ObjRef,
    ...(opts.articleBody ? { articleBody: opts.articleBody } : {}),
  };
}

/**
 * The publication licence attached to every BizRnR research Dataset.
 *
 * Google's Dataset rich-result spec lists `license` as a recommended field, and
 * GSC reports "Missing field license" against any Dataset node that omits it.
 * More importantly for AEO: an explicit, permissive licence is what lets an AI
 * assistant reuse our evidence tables *with attribution* rather than skipping
 * them. CC BY 4.0 was already the declared licence on the 2026 State-of-the-
 * industry report dataset; this constant makes that choice the SSOT so every
 * Dataset we publish agrees.
 */
export const DATASET_LICENSE = 'https://creativecommons.org/licenses/by/4.0/';

/**
 * Build a Dataset node with the house licence, publisher, and free-access flags
 * already applied.
 *
 * Every field Google treats as recommended for the Dataset rich result is
 * either defaulted here or accepted as an option, so a caller cannot ship a
 * Dataset that is missing `license` (the 2026-07-24 GSC finding). Pass
 * `distribution` entries as plain `{ encodingFormat, contentUrl }` pairs; the
 * helper wraps them in DataDownload.
 */
export function buildDatasetSchema(opts: {
  id: string;
  name: string;
  description: string;
  url: string;
  dateModified?: Date | string;
  datePublished?: Date | string;
  creator?: JsonLdObject | ObjRef;
  citation?: readonly string[];
  isBasedOn?: readonly string[];
  keywords?: readonly string[];
  measurementTechnique?: string;
  variableMeasured?: readonly string[];
  distribution?: readonly { encodingFormat: string; contentUrl: string }[];
  license?: string;
  isAccessibleForFree?: boolean;
}): JsonLdObject {
  const toIso = (v: Date | string): string => (typeof v === 'string' ? v : v.toISOString());

  const node: JsonLdObject = {
    '@type': 'Dataset',
    '@id': opts.id,
    name: opts.name,
    description: opts.description,
    url: opts.url,
    license: opts.license ?? DATASET_LICENSE,
    isAccessibleForFree: opts.isAccessibleForFree ?? true,
    creator: opts.creator ?? ({ '@id': ORG_ID } satisfies ObjRef),
    publisher: { '@id': ORG_ID } satisfies ObjRef,
  };

  if (opts.datePublished) node.datePublished = toIso(opts.datePublished);
  if (opts.dateModified) node.dateModified = toIso(opts.dateModified);
  if (opts.citation?.length) node.citation = [...opts.citation];
  if (opts.isBasedOn?.length) node.isBasedOn = [...opts.isBasedOn];
  if (opts.keywords?.length) node.keywords = [...opts.keywords];
  if (opts.measurementTechnique) node.measurementTechnique = opts.measurementTechnique;
  if (opts.variableMeasured?.length) node.variableMeasured = [...opts.variableMeasured];
  if (opts.distribution?.length) {
    node.distribution = opts.distribution.map((d) => ({
      '@type': 'DataDownload',
      encodingFormat: d.encodingFormat,
      contentUrl: d.contentUrl,
    }));
  }

  return node;
}

/**
 * Drop schema nodes whose required arrays are empty / missing. Emits an
 * empty `mainEntity: []` on FAQPage (or empty `itemListElement` on
 * BreadcrumbList / ItemList) is a Schema.org error and Ahrefs/SEO Guard
 * flag it. The cause is almost always a page that derives its array
 * from optional source data (e.g. `AI_BUSINESS_PAGE_COPY.faqs ?? []`)
 * and ships the empty fallback. Filter at the helper boundary so a
 * single oversight in one page can't fail validate-schema globally.
 *
 * Rule per @type:
 *   FAQPage         — must have non-empty mainEntity[]
 *   ItemList        — must have non-empty itemListElement[]
 *   BreadcrumbList  — must have non-empty itemListElement[]
 *   HowTo           — must have non-empty step[]
 */
export function isStructurallyValidNode(node: JsonLdObject): boolean {
  const t = node['@type'];
  if (t === 'FAQPage') {
    const me = node['mainEntity'];
    return Array.isArray(me) && me.length > 0;
  }
  if (t === 'ItemList' || t === 'BreadcrumbList') {
    const items = node['itemListElement'];
    return Array.isArray(items) && items.length > 0;
  }
  if (t === 'HowTo') {
    const steps = node['step'];
    return Array.isArray(steps) && steps.length > 0;
  }
  return true;
}

/**
 * Wrap an array of schema nodes in a valid top-level @context + @graph
 * envelope. This is the ONLY safe way to ship multiple nodes from one
 * <script type="application/ld+json"> — a bare array fails validation
 * (that bug drove 355+ Ahrefs errors on /business/[slug] and was the
 * primary fix in the 2026-04-19 series).
 *
 * Defensively drops structurally-invalid nodes (empty mainEntity on
 * FAQPage, etc. — see `isStructurallyValidNode`). This is the cure
 * for the recurring "FAQPage.mainEntity must be a non-empty array"
 * SEO Guard / validate-schema failure.
 */
export function wrapGraph(nodes: JsonLdObject[]): JsonLdObject {
  return {
    '@context': 'https://schema.org',
    '@graph': nodes.filter(isStructurallyValidNode),
  };
}
